import { sessionSelectedAtomFamily } from '@/features/consolidatedSessionsList/atoms';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

/** Stable no-op atom for edge cases without a valid ID */
const noopSelectedAtom = atom(
  () => false,
  () => {}
);

/** Resolves the correct atom for a sessionId (real or no-op) with a stable reference */
const useSelectedAtom = (sessionId: string) =>
  useMemo(
    () => (sessionId ? sessionSelectedAtomFamily(sessionId) : noopSelectedAtom),
    [sessionId]
  );

/**
 * Read-only hook for checking if a session is selected.
 * Use this for neighbor reads (e.g. adjacency joining) where write access is not needed.
 */
export const useSessionSelectedValue = (sessionId: string): boolean =>
  useAtomValue(useSelectedAtom(sessionId));

/**
 * Hook for individual session selection - provides O(1) updates with granular subscriptions.
 * When sessionId is empty, returns a stable false/no-op to avoid polluting the atomFamily cache.
 */
export const useConsolidatedSessionSelection = (sessionId: string) => {
  const selectedAtom = useSelectedAtom(sessionId);
  const isSelected = useAtomValue(selectedAtom);
  const setSelected = useSetAtom(selectedAtom);

  return {
    isSelected,
    setSelected,
  };
};
